import config from '../oktaConfig';
import {useCallback, useRef} from 'react';
import {useOktaAuth} from '@okta/okta-react';
import {buildUserLog} from 'utils/logUsers';
import {patchVersion} from 'constants/releaseConfig';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';

export default function useUserLogs() {
  const location = useLocation();
  const fullPath = (location?.pathname ?? '') + (location?.search ?? '');
  const {oktaAuth} = useOktaAuth();

  // Current User Data
  const themeSelected = useSelector(
    (state: Record<string, Record<string, string>>) => state.theme.select
  );
  const sessionId = useSelector(
    (state: Record<string, Record<string, string>>) => state.userInfo.sessionId
  );

  const abortControllerRef = useRef<AbortController | null>(null);
  if (abortControllerRef.current === null) {
    abortControllerRef.current = new AbortController();
  }

  const dispatch = useCallback(
    async (action: string, payload?: Record<string, any>) => {
      const accessToken = oktaAuth.getAccessToken();

      const userInfo = await oktaAuth.getUser().catch(err => {
        console.error(err);
        return null;
      });

      const userLogObj = buildUserLog(
        userInfo?.name ?? null,
        userInfo?.email ?? null,
        new Date().getTime(),
        sessionId,
        themeSelected,
        fullPath,
        action,
        payload,
        patchVersion.version
      );

      return fetch(config.resourceServer.insightsApiUrlLogUserAction, {
        mode: 'cors',
        method: 'post',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        signal: abortControllerRef.current!.signal,
        body: JSON.stringify(userLogObj),
        keepalive: true,
      })
        .then(response => {
          if (!response.ok) {
            return Promise.reject();
          }
        })
        .catch(err => {
          console.error(err);
        });
    },
    [oktaAuth, sessionId, themeSelected, fullPath]
  );

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
  }, []);

  return {dispatch, abort};
}
